/**
 * Shared open-house FORMAT helpers (home v1 reskin simplify pass) — the one
 * home for the day-palette/label/price/beds-baths derivations that the
 * open-house surfaces (`components/open-house-map.tsx`,
 * `components/home/OpenHouseWeekExplorer.tsx`) previously each carried a copy
 * of. Pure functions + constants only — no React, no DB — so both server and
 * client modules can import it (`lib/data/open-houses.ts` pins its SQL
 * day-of-week derivation to the same `MARKET_TIME_ZONE`).
 *
 * FOLLOW-UP (deliberately NOT consolidated here): time-of-day DISPLAY pinning
 * differs by surface — the map popup and the profile explorer format times in
 * the VIEWER'S local zone, while the home week explorer pins display to
 * `MARKET_TIME_ZONE`. Those `Intl.DateTimeFormat` time formatters stay local to
 * their components until that inconsistency is resolved as its own change.
 */

/**
 * The market zone every open-house DAY derivation is pinned to — matches the
 * SQL `extract(dow … at time zone …)` in `lib/data/open-houses.ts`, so client
 * re-derivations (`nyWeekdayIndex`) and the read layer's `dayOfWeek` agree.
 */
export const MARKET_TIME_ZONE = "America/New_York";

/** Day labels keyed 0=Sun..6=Sat — the contract `--brand-oh-day-0..6` keys on. */
export const DAY_LABELS = [
  "Sun",
  "Mon",
  "Tue",
  "Wed",
  "Thu",
  "Fri",
  "Sat",
] as const;

/** The day-palette CSS var for a 0=Sun..6=Sat index (defined in globals.css).
 * Out-of-range input is normalized mod 7 (negative-safe). */
export function dayVar(dayOfWeek: number): string {
  const d = ((dayOfWeek % 7) + 7) % 7;
  return `var(--brand-oh-day-${d})`;
}

/**
 * The soft fill derived from a day's hue — the ONE tint formula shared by the
 * home 7-day schedule bars AND the map's price pins / legend swatches, so the
 * two surfaces render the SAME color per day (UI styling pass 2026-06-12:
 * schedule and map colors must match up).
 */
export function dayTint(dayOfWeek: number): string {
  return `color-mix(in srgb, ${dayVar(dayOfWeek)} 20%, var(--brand-background))`;
}

// Pinned 'en-US' + market zone — the short weekday name feeds the index lookup.
const NY_WEEKDAY_FMT = new Intl.DateTimeFormat("en-US", {
  weekday: "short",
  timeZone: MARKET_TIME_ZONE,
});

/**
 * Market-zone weekday index (0=Sun..6=Sat) for an ISO timestamp — the same
 * formatter + `DAY_LABELS.indexOf` derivation both explorers used locally.
 * Null when the input is absent, unparseable, or (theoretically) unmatched.
 */
export function nyWeekdayIndex(iso: string | null): number | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const idx = (DAY_LABELS as readonly string[]).indexOf(NY_WEEKDAY_FMT.format(d));
  return idx >= 0 ? idx : null;
}

// formatPriceCompact moved to `src/lib/format.ts` (it outgrew the open-house
// scope — the property page and profile cards consume it too).

/**
 * Null-guarded "3 bd · 2.5 ba" line ('en-US' pinned) — a missing part drops
 * cleanly (no dangling separator); both missing → "" (falsy, so callers can
 * conditionally render).
 */
export function formatBedsBaths(
  beds: number | null,
  baths: number | null,
): string {
  return [
    beds != null ? `${beds.toLocaleString("en-US")} bd` : null,
    baths != null ? `${baths.toLocaleString("en-US")} ba` : null,
  ]
    .filter(Boolean)
    .join(" · ");
}
